import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { CreditCard } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { usePricing, zoneFromLang, ZONE_LABELS } from '../lib/pricing';
import { useLang } from '../i18n/LanguageContext';
import { localizeKitName } from '../lib/productName';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CKX = {
  fr: { totalHT: 'Total HT', vat: 'TVA', totalTTC: 'Total TTC', debited: 'Montant débité : le total TTC.', deliveryCountry: 'Pays de livraison / facturation', reseller: "Aux Émirats, les commandes sont traitées par notre revendeur local : le paiement en ligne n'est pas disponible. Envoyez-nous votre demande, vous recevez un devis en AED.", requestQuote: 'Demander un devis', ttc: 'TTC' },
  en: { totalHT: 'Subtotal (excl. VAT)', vat: 'VAT', totalTTC: 'Total (incl. VAT)', debited: 'Amount charged: the total incl. VAT.', deliveryCountry: 'Delivery / billing country', reseller: 'In the UAE, orders are handled by our local reseller: online payment is not available. Send us your request and you will receive a quote in AED.', requestQuote: 'Request a quote', ttc: 'incl. VAT' },
  ar: { totalHT: 'المجموع بدون ضريبة', vat: 'ضريبة القيمة المضافة', totalTTC: 'المجموع شامل الضريبة', debited: 'المبلغ المخصوم: المجموع شامل الضريبة.', deliveryCountry: 'بلد التوصيل / الفوترة', reseller: 'في الإمارات، تتم معالجة الطلبات عبر موزّعنا المحلي: الدفع عبر الإنترنت غير متاح. أرسل طلبك وستتلقى عرض سعر بالدرهم.', requestQuote: 'اطلب عرض سعر', ttc: 'شامل الضريبة' },
  it: { totalHT: 'Totale IVA escl.', vat: 'IVA', totalTTC: 'Totale IVA incl.', debited: "Importo addebitato: il totale IVA incl.", deliveryCountry: 'Paese di consegna / fatturazione', reseller: "Negli Emirati gli ordini sono gestiti dal nostro rivenditore locale: il pagamento online non è disponibile. Inviaci la tua richiesta e riceverai un preventivo in AED.", requestQuote: 'Richiedi un preventivo', ttc: 'IVA incl.' },
};

const Checkout = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [methods, setMethods] = useState({ stripe: true, paypal: true });
  const { lang, t } = useLang();
  const [country, setCountry] = useState(zoneFromLang(lang));
  const pricing = usePricing(lang, country);
  const tc = t.checkout;
  const ck = CKX[lang] || CKX.fr;

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/payment/methods`)
      .then((res) => {
        setMethods(res.data);
        if (!res.data.stripe && res.data.paypal) setPaymentMethod('paypal');
      })
      .catch(() => {});
  }, []);

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: ''
  });

  React.useEffect(() => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const groupedItems = cart.reduce((acc, item) => {
      const existing = acc.find(i => i.id === item.id);
      if (existing) {
        existing.quantity += 1;
      } else {
        acc.push({ ...item, quantity: 1 });
      }
      return acc;
    }, []);
    setCartItems(groupedItems);
  }, []);

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (pricing.htOf(item) * item.quantity), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pricing.checkoutEnabled) {
      toast({ title: 'Paiement en ligne indisponible dans votre zone', description: 'Votre demande est traitée par notre revendeur local : contactez-nous pour un devis.' });
      navigate('/contact');
      return;
    }
    setLoading(true);

    try {
      // 1. Create order
      const orderData = {
        ...formData,
        items: cartItems.map(item => ({
          productId: item.id,
          productName: item.name,
          price: pricing.htOf(item),
          quantity: item.quantity,
          image: item.images ? item.images[0] : '',
          marking: item._marking || 'none',
          logoUrl: item._logoUrl || null,
          customText: item._customText || null
        })),
        totalAmount: pricing.ttcOn(getTotalPrice()),
        subtotalHT: getTotalPrice(),
        vatRate: pricing.vatRate,
        billingCountry: country,
        vatAmount: pricing.vatOn(getTotalPrice())
      };

      const orderResponse = await axios.post(`${BACKEND_URL}/api/orders`, orderData);
      const orderId = orderResponse.data.id;

      // 2. Create payment session - send origin_url (amount is fetched server-side)
      const originUrl = window.location.origin;

      if (paymentMethod === 'stripe') {
        const paymentResponse = await axios.post(`${BACKEND_URL}/api/payment/stripe/create-session`, {
          order_id: orderId,
          origin_url: originUrl
        });
        
        // Clear cart before redirect
        localStorage.setItem('cart', JSON.stringify([]));
        window.dispatchEvent(new Event('cartUpdated'));
        
        // Redirect to Stripe
        window.location.href = paymentResponse.data.url;
        
      } else if (paymentMethod === 'paypal') {
        const paymentResponse = await axios.post(`${BACKEND_URL}/api/payment/paypal/create-order`, {
          order_id: orderId,
          origin_url: originUrl
        });
        
        localStorage.setItem('cart', JSON.stringify([]));
        window.dispatchEvent(new Event('cartUpdated'));
        window.location.href = paymentResponse.data.approval_url;
      }

    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer le paiement",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20" data-testid="empty-cart">
          <h2 className="text-3xl font-bold text-white mb-4">{tc.emptyCart}</h2>
          <p className="text-gray-400 mb-8">{tc.emptyCartDesc}</p>
          <Button onClick={() => navigate('/catalogue')} className="bg-purple-600 hover:bg-purple-700" data-testid="go-catalogue-btn">
            {tc.viewCatalogue}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Georgia, serif' }} data-testid="checkout-title">
          {tc.title}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Form */}
          <div className="bg-gray-900 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-white mb-6">{tc.deliveryInfo}</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="checkout-form">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{tc.name} *</label>
                <Input
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  required
                  className="bg-gray-800 text-white border-gray-700"
                  placeholder="Jean Dupont"
                  data-testid="checkout-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{tc.email} *</label>
                <Input
                  type="email"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleChange}
                  required
                  className="bg-gray-800 text-white border-gray-700"
                  placeholder="jean.dupont@email.com"
                  data-testid="checkout-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{tc.phone} *</label>
                <Input
                  type="tel"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleChange}
                  required
                  className="bg-gray-800 text-white border-gray-700"
                  placeholder="+33 6 12 34 56 78"
                  data-testid="checkout-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{tc.address} *</label>
                <Textarea
                  name="customerAddress"
                  value={formData.customerAddress}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="bg-gray-800 text-white border-gray-700"
                  placeholder="123 Rue Example, 75001 Paris, France"
                  data-testid="checkout-address"
                />
              </div>

              {/* Payment Method Selection — uniquement FR/IT (zone avec paiement en ligne) */}
              {pricing.checkoutEnabled && (
              <div className="pt-4">
                <label className="block text-sm font-medium text-gray-300 mb-4">{tc.paymentMethod} *</label>
                
                <div className="space-y-3">
                  {methods.stripe && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('stripe')}
                    className={`w-full p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'stripe'
                        ? 'border-purple-600 bg-purple-600/20'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                    data-testid="payment-stripe-btn"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <CreditCard className="w-6 h-6 text-white" />
                        <div className="text-left">
                          <p className="text-white font-semibold">{tc.stripe}</p>
                          <p className="text-gray-400 text-sm">{tc.stripeDesc}</p>
                        </div>
                      </div>
                      {paymentMethod === 'stripe' && (
                        <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        </div>
                      )}
                    </div>
                  </button>
                  )}

                  {methods.paypal && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('paypal')}
                    className={`w-full p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'paypal'
                        ? 'border-blue-600 bg-blue-600/20'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                    data-testid="payment-paypal-btn"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-white">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                        </svg>
                        <div className="text-left">
                          <p className="text-white font-semibold">{tc.paypal}</p>
                          <p className="text-gray-400 text-sm">{tc.paypalDesc}</p>
                        </div>
                      </div>
                      {paymentMethod === 'paypal' && (
                        <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        </div>
                      )}
                    </div>
                  </button>
                  )}

                  {!methods.stripe && !methods.paypal && (
                    <p className="text-yellow-300/90 text-sm p-4 rounded-lg border border-yellow-600/30 bg-yellow-900/20" data-testid="no-payment-method">
                      Le paiement en ligne est momentanément indisponible. Contactez-nous et nous finalisons votre commande ensemble.
                    </p>
                  )}
                </div>
              </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-4 text-lg font-semibold mt-6"
                data-testid="checkout-pay-btn"
              >
                {loading ? tc.redirecting : (pricing.checkoutEnabled ? `${tc.pay} ${pricing.fmt(pricing.ttcOn(getTotalPrice()))} ${ck.ttc}` : ck.requestQuote)}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-gray-900 rounded-lg p-8 sticky top-24" data-testid="order-summary">
              <h2 className="text-2xl font-bold text-white mb-6">{tc.summary}</h2>
              
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 pb-4 border-b border-gray-800">
                    <img loading="lazy"
                      src={item.images ? item.images[0] : ''}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-sm">{localizeKitName(item.name, lang)}</h3>
                      <p className="text-gray-400 text-sm">{tc.quantity}: {item.quantity}</p>
                    </div>
                    <p className="text-white font-bold">
                      {pricing.fmt(pricing.ttcOn(pricing.htOf(item) * item.quantity))} TTC
                    </p>
                  </div>
                ))}
              </div>

              <div className="space-y-2 mb-6" data-testid="checkout-totals">
                <div className="flex justify-between text-gray-300">
                  <span>{ck.totalHT}</span>
                  <span data-testid="checkout-subtotal-ht">{pricing.fmt(getTotalPrice())}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>{ck.vat} {pricing.vatRate} %</span>
                  <span data-testid="checkout-vat">{pricing.fmt(pricing.vatOn(getTotalPrice()))}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>{tc.shipping}</span>
                  <span className="text-green-500">{tc.free}</span>
                </div>
                <div className="border-t border-gray-700 pt-2">
                  <div className="flex justify-between text-xl font-bold text-white">
                    <span>{ck.totalTTC}</span>
                    <span data-testid="checkout-total-ttc">{pricing.fmt(pricing.ttcOn(getTotalPrice()))}</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">{ck.debited}</p>
                  <label className="block mt-4 text-xs text-gray-400">
                    {ck.deliveryCountry}
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="w-full mt-1 bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                      data-testid="billing-country"
                    >
                      <option value="FR">{ZONE_LABELS.FR}</option>
                      <option value="IT">{ZONE_LABELS.IT}</option>
                      <option value="AE">{ZONE_LABELS.AE}</option>
                    </select>
                  </label>
                  {!pricing.checkoutEnabled && (
                    <div className="mt-3 bg-amber-900/30 border border-amber-700/50 rounded-lg p-3" data-testid="reseller-notice">
                      <p className="text-amber-200 text-xs">
                        {ck.reseller}
                      </p>
                      <Button
                        type="button"
                        onClick={() => navigate('/contact')}
                        className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-black text-xs"
                        data-testid="reseller-contact-btn"
                      >
                        {ck.requestQuote}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {pricing.checkoutEnabled && (
              <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4">
                <p className="text-blue-300 text-sm">
                  {tc.secure}
                </p>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
